'use client'
import React, { useEffect, useState } from 'react'
import MyImage from '../myImage'
import { Col, Row } from '../row'
import CustomLink from '../customLink'
import useDebounce from '@/shared/hooks/useDebounce'
import { getImgURL, parallelCalls } from '@/shared/utils'
import { useLazyQuery } from '@apollo/client'
import {
  GET_SEARCH_NEWS,
  GET_SEARCH_PLAYER,
  GET_SEARCH_SERIES,
  GET_SEARCH_TEAM
} from '@/graphql/search/search.query'
import PlayerPlaceholder from '@/assets/images/placeholder/player.svg'
import TeamPlaceholder from '@/assets/images/placeholder/team.png'
import SeriesPlaceholder from '@/assets/images/placeholder/series.svg'
import articlePlaceholder from '@/assets/images/placeholder/articlePlaceholder.svg'
import { ClockIcon, SearchIcon } from '@/shared/Icons'
import { BLUR_DATA_URL_BASE64, rolesFull } from '@/shared/constants'

const getSearchPayload = (search: string | undefined, limit: number) => {
  return {
    nLimit: limit,
    nSkip: 1,
    sSearch: search,
    sSortBy: 'dCreated',
    nOrder: -1
  }
}

export default function SearchModalBox({ onClose }: any) {
  const [searchVal, setSearchVal] = useState('')
  const [suggestions, setSuggestions] = useState<any>({})
  const [loading, setLoading] = useState(false)

  const [searchPlayer] = useLazyQuery(GET_SEARCH_PLAYER)
  const [searchSeries] = useLazyQuery(GET_SEARCH_SERIES)
  const [searchTeam] = useLazyQuery(GET_SEARCH_TEAM)
  const [searchNews] = useLazyQuery(GET_SEARCH_NEWS)

  function onResults(data: any) {
    const [player, series, team, news] = data
    setSuggestions({
      player: player?.getPlayerSearch?.aResults?.length > 0 ? player.getPlayerSearch.aResults : [],
      series: series?.getSeriesSearch?.aResults?.length > 0 ? series.getSeriesSearch.aResults : [],
      team: team?.getTeamSearch?.aResults?.length > 0 ? team.getTeamSearch.aResults : [],
      news: news?.getArticleSearch?.aResults?.length > 0 ? news.getArticleSearch.aResults : []
    })
    setLoading(false)
  }

  const changeSearch = async (val: string) => {
    try {
      if (!val || val.length < 3) {
        return // or do nothing
      }
      setLoading(true)

      if (!val.endsWith(' ')) {
        const data = await parallelCalls([
          searchPlayer({ variables: { input: getSearchPayload(val, 4) } }),
          searchSeries({ variables: { input: getSearchPayload(val, 4) } }),
          searchTeam({ variables: { input: getSearchPayload(val, 4) } }),
          searchNews({ variables: { input: getSearchPayload(val, 3) } })
        ])
        onResults(data)
      } else {
        setLoading(false)
      }
    } catch (err) {
      setLoading(false)
    }
  }

  const enhancedSearch = useDebounce(searchVal, 500)

  useEffect(() => {
    changeSearch(enhancedSearch)
  }, [enhancedSearch])

  function onLinkOpen() {
    onClose()
  }

  return (
    <div className="h-screen w-full fixed top-0 left-0 z-50">
      <div
        className="absolute top-0 left-0 w-full h-full bg-black/30 bg-opacity-30"
        onClick={onClose}
      ></div>
      <div className="absolute top-[84px] left-2/4 -translate-x-2/4 w-[calc(100%_-_20px)] max-w-[863px] rounded-lg mx-auto">
        <div className="flex flex-col items-center justify-center h-full ">
          <div className=" relative w-full">
            <input
              type="text"
              autoFocus
              onChange={e => setSearchVal(e.target.value)}
              className={`w-full p-2 ${
                enhancedSearch ? 'border-b-2' : ''
              } bg-headersActive placeholder:text-textDselected text-textOffWhite pr-11 text-xs md:text-base font-medium border border-borderStroke px-5 py-3.5 md:py-4 rounded-xl`}
              placeholder="Players, Series, Teams & More"
            />
            <span className=" absolute top-2/4 -translate-y-2/4 right-5 text-textDselected">
              <SearchIcon />
            </span>
          </div>
          {/* rounded-b-none border-b-0  */}
          {enhancedSearch ? (
            <div className="h-[400px] md:h-[536px] overflow-auto w-full bg-headersActive rounded-2xl p-4 md:p-6 mt-5">
              {!loading && (
                <>
                  <div className="flex flex-col md:grid md:grid-cols-3 md:gap-3">
                    {suggestions?.player?.length ? (
                      <div className="pb-4 md:pb-0 border-borderStroke md:border-b-0 border-b">
                        <h6 className="text-base  md:text-lg text-textOffWhite font-bold mb-5 md:mb-6">
                          Players
                        </h6>
                        {suggestions?.player?.map((player: any) => {
                          return (
                            <div
                              className=" mb-4 md:mb-6 last:mb-0"
                              key={player?._id}
                              onClick={onLinkOpen}
                            >
                              <PlayerCard player={player} />
                            </div>
                          )
                        })}
                      </div>
                    ) : null}
                    {suggestions?.team?.length ? (
                      <div className="pt-4 md:pt-0 pb-4 md:pb-0 border-borderStroke md:border-b-0 border-b">
                        <h6 className="text-base  md:text-lg text-textOffWhite font-bold mb-5 md:mb-6">
                          Teams
                        </h6>
                        {suggestions?.team?.map((team: any) => {
                          return (
                            <div
                              className=" mb-4 md:mb-6 last:mb-0"
                              key={team?._id}
                              onClick={onLinkOpen}
                            >
                              <TeamCard team={team} />
                            </div>
                          )
                        })}
                      </div>
                    ) : null}
                    {suggestions?.series?.length ? (
                      <div className="pt-4 md:pt-0">
                        <h6 className="text-base  md:text-lg text-textOffWhite font-bold mb-5 md:mb-6">
                          Series
                        </h6>
                        {suggestions?.series?.map((series: any) => {
                          return (
                            <div
                              className=" mb-4 md:mb-6 last:mb-0"
                              key={series?._id}
                              onClick={onLinkOpen}
                            >
                              <SeriesCard series={series} />
                            </div>
                          )
                        })}
                      </div>
                    ) : null}
                  </div>
                  {suggestions?.news?.length ? (
                    <div className="pt-4 mt-4 border-t border-borderStroke md:mt-7 md:pt-7">
                      <h6 className="text-base  md:text-lg text-textOffWhite font-bold mb-4">
                        News
                      </h6>
                      <Row className="gap-4 md:gap-7">
                        {suggestions?.news?.map((article: any) => {
                          return (
                            <Col key={article?._id} onClick={onLinkOpen} xs={12} md={6}>
                              <NewsCard article={article} />
                            </Col>
                          )
                        })}
                      </Row>
                    </div>
                  ) : null}
                  {Object.values(suggestions).every((item: any) => item?.length === 0) ? (
                    <div className="flex flex-col items-center justify-center h-full">
                      <p className="text-center text-lg">No results found</p>
                    </div>
                  ) : null}
                </>
              )}
              {loading ? (
                <h4
                  className={`font-medium text-base sm:pl-4 sm:py-3 mt-6 relative flex items-center justify-center h-full`}
                >
                  <span>
                    Loading<span className="text-primary-500">...</span>
                  </span>{' '}
                  <span className="animate-ping h-2 w-2 ml-3 rounded-full bg-primary-500"></span>
                </h4>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function PlayerCard({ player }: any) {
  return (
    <CustomLink
      href={player?.oSeo?.sSlug ? `/${player?.oSeo?.sSlug}` : ''}
      className="flex items-center gap-4 text-textOffWhite"
    >
      <MyImage
        src={player?.oImg?.sUrl ? getImgURL(player?.oImg?.sUrl) : PlayerPlaceholder}
        width={48}
        height={48}
        className="size-12 object-cover rounded-full flex-shrink-0"
      />
      <div>
        <span className="text-sm md:text-base font-medium line-clamp-1">
          {player?.sTitle || player?.sFullName}
        </span>
        <span className="text-xs md:text-sm text-textDselected mt-0.5">
          {rolesFull[player?.sPlayingRole]}
        </span>
      </div>
    </CustomLink>
  )
}
function TeamCard({ team }: any) {
  return (
    <CustomLink
      href={team?.oSeo?.sSlug ? `/${team?.oSeo?.sSlug}` : ''}
      className="flex items-center gap-4 text-textOffWhite"
    >
      <MyImage
        src={team?.oImg?.sUrl ? getImgURL(team?.oImg?.sUrl) : TeamPlaceholder}
        width={48}
        height={48}
        className="size-12 object-cover rounded-full flex-shrink-0"
      />
      <div className="group">
        <span className="text-sm md:text-base font-medium line-clamp-1">
          {team?.sTitle || team?.sFullName}
        </span>
        <span className="text-xs md:text-sm text-textDselected mt-0.5">Teams</span>
      </div>
    </CustomLink>
  )
}

function SeriesCard({ series }: any) {
  return (
    <CustomLink
      href={series?.oSeo?.sSlug ? `/${series?.oSeo?.sSlug}` : ''}
      className="flex items-center gap-4 text-textOffWhite"
    >
      <MyImage
        src={
          series?.oCategory?.oImg?.sUrl
            ? getImgURL(series?.oCategory?.oImg?.sUrl)
            : SeriesPlaceholder
        }
        width={48}
        height={48}
        className="size-12 object-cover rounded-full flex-shrink-0"
      />
      <span className="text-sm md:text-base font-medium">
        {series?.sTitle || series?.sFullName}
      </span>
    </CustomLink>
  )
}
function NewsCard({ article }: any) {
  return (
    <CustomLink
      href={article?.oSeo?.sSlug ? `/${article?.oSeo?.sSlug}` : ''}
      id={article?._id}
      className="rounded-xl border border-transparent  flex gap-4 transition-all group"
      key={article._id}
    >
      <div className="w-[30%] rounded-lg aspect-video overflow-hidden">
        <MyImage
          src={article?.oImg?.sUrl ? getImgURL(article?.oImg?.sUrl) : articlePlaceholder}
          alt={article?.oImg?.sText || article?.sTitle || 'No Title'}
          placeholder="blur"
          placeholderImage={articlePlaceholder}
          blurDataURL={BLUR_DATA_URL_BASE64}
          height={84}
          width={140}
          className="rounded-lg object-cover h-full w-full"
        />
      </div>
      <div className="flex flex-col text-white w-[70%] ">
        <h3 className="text-textOffWhite mb-3 md:mb-4 font-medium text-xs md:text-base group-hover:text-primary-500 line-clamp-2 transition-al leading-5 md:leading-normal">
          {article?.sTitle || 'No Title'}
        </h3>

        <div className="flex flex-row items-center gap-2 text-textDselected">
          <ClockIcon />
          <span className="text-xs md:text-sm text-textDselected">
            {article?.nDuration ?? 'N/A'} Mins
          </span>
        </div>
      </div>
    </CustomLink>
  )
}
